import React, { useState, FC, MouseEvent } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useEditorState,
  useEditorStateLinkContentGetter,
  useProvidersCurrentEmployee,
} from "~/hooks";

import { TextEditor } from "~/components/organisms";

import { InsightPostCommentDetailWithEditForm } from "./InsightPostCommentDetailWithEditForm";
import { InsightPostDetail } from "../InsightPostDetail";

import {
  InsightPostType,
  InsightPostCommentType,
  MentionSuggestEmployeeType,
  PreviewWithFileType,
  ApiMemberInsightPostsInsightPostCommentsIndexType,
  ApiMembersInsightPostsAiCommentRequestsMutateResponseType,
  AuthorTypeConst,
} from "~/domains";

import {
  useApisInsightPostsInsightPostCommentsCreate,
  useApisInsightPostsAiCommentRequestsCreate,
} from "~/features/insight-posts/hooks/members";
import { Button, DotLoadingAnimation } from "~/ui";

type PropsType = {
  highlightInsightPostCommentId?: string;
  insightPost: InsightPostType;
  insightPostComments: InsightPostCommentType[];
  mentions: MentionSuggestEmployeeType[];
  className?: string;
  refetch: () => Promise<
    QueryObserverResult<
      ApiMemberInsightPostsInsightPostCommentsIndexType,
      unknown
    >
  >;
};

export const InsightPostCommentsWithInsightPost: FC<PropsType> = ({
  highlightInsightPostCommentId = "",
  insightPost,
  insightPostComments,
  mentions,
  className = "",
  refetch,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();

  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      value: editorState,
      onChange: setEditorState,
      jsonContent,
      hasText,
      setPlainText,
    },
  ] = useEditorState();
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState,
    isWatch: true,
  });

  const { mutate, isLoading } = useApisInsightPostsInsightPostCommentsCreate();

  const {
    mutate: mutateAiCommentRequest,
    isLoading: isLoadingAiCommentRequest,
  } = useApisInsightPostsAiCommentRequestsCreate();
  const isRequestingAiComment =
    !!insightPost.inProgressAiCommentGenerationRequest ||
    isLoadingAiCommentRequest;

  const handleAiCommentRequest = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    mutateAiCommentRequest(
      { insightPostId: insightPost.id },
      {
        onSuccess: (
          data: ApiMembersInsightPostsAiCommentRequestsMutateResponseType,
        ) => {
          toast.success(data.message);
        },
      },
    );
  };

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!hasText) {
      toast.error("テキストが空です");
      return;
    }
    const body = {
      content: jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };

    mutate(
      {
        insightPostId: insightPost.id,
        body,
      },
      {
        onSuccess: async () => {
          setPlainText("");
          setFiles([]);
          await refetch();
        },
      },
    );
  };

  const lastComment = insightPostComments[insightPostComments.length - 1];

  return (
    <div className={className}>
      <div className="space-y-6">
        <InsightPostDetail
          insightPost={insightPost}
          menuItems={[]}
          openComment={() => refetch()}
        />
        <div>
          {insightPostComments.map((comment) => (
            <InsightPostCommentDetailWithEditForm
              key={comment.id}
              insightPostComment={comment}
              mentions={mentions}
              refetch={refetch}
              isHighlight={comment.id === highlightInsightPostCommentId}
            />
          ))}
        </div>
        {currentEmployee?.id === insightPost.employee.id &&
          (isRequestingAiComment ? (
            <DotLoadingAnimation className="justify-center" />
          ) : (
            // コメントが存在しない または コメントの作者が従業員の場合
            (!lastComment ||
              lastComment?.author.type === AuthorTypeConst.EMPLOYEE) && (
              <Button
                text={"かがみんに聞く"}
                color={"primary"}
                outline
                onClick={handleAiCommentRequest}
                readonly={isRequestingAiComment}
                className="w-full"
              />
            )
          ))}
        <TextEditor
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          editorState={editorState}
          setEditorState={setEditorState}
          files={files}
          onChangeFiles={setFiles}
          blockQuote={fetchedData}
          isEmojiPickerTop
        />
      </div>
    </div>
  );
};
